import React from 'react';
import { Layers, LayersClear, LayersTwoTone } from '@material-ui/icons';
import PropTypes from 'prop-types';
import Empty from './Empty';
import { makeStyles } from '@material-ui/core';

/**
 * @typedef {import('src/lib/type').CategorySearchHit} CategorySearchHit
 * @typedef {import('src/lib/type').SearchHitType} SearchHitType
 */

const useStyles = makeStyles((theme) => ({
  taxonomySection: {
    backgroundColor: '#fff',
    borderBottom: '1px solid #e5e7eb',
    borderTopLeftRadius: theme.shape.borderRadius,
    [theme.breakpoints.up('md')]: {
      borderBottom: 'none',
      borderRight: '1px solid #e5e7eb',
    },
    [theme.breakpoints.down('sm')]: {
      '&.empty-suggestions': {
        display: 'none',
      },
    },
  },
  sectionHeader: {
    position: 'sticky',
    top: 0,
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb',
    zIndex: 10,
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#1f2937',
    margin: 0,
  },
  sectionCount: {
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: 'normal',
  },
  taxonomyList: {
    padding: '8px',
    maxHeight: '600px',
    overflowY: 'auto',
  },
  taxonomyItem: {
    width: '100%',
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(59, 130, 246, 0.08)',
    },
    '&:hover $taxonomyIcon': {
      backgroundColor: 'rgba(243, 244, 246, 0.8)',
    },
    '&:hover $taxonomyTitle': {
      color: '#1f2937',
    },
    '& em': {
      fontStyle: 'normal',
      color: theme.palette.primary.main,
    },
  },
  taxonomyIcon: {
    width: '32px',
    height: '32px',
    backgroundColor: 'rgba(243, 244, 246, 0.5)',
    borderRadius: '4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    transition: 'background-color 0.2s ease',
  },
  taxonomyContent: {
    flex: 1,
    minWidth: 0,
  },
  taxonomyTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'rgba(31, 41, 55, 0.8)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    transition: 'color 0.2s ease',
  },
  taxonomyHierarchy: {
    fontSize: '12px',
    color: '#6b7280',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: '2px',
  },
  icon: {
    fontSize: '16px',
    color: theme.palette.primary.main,
  },
}));

/**
 * Component for displaying taxonomy results (categories, subcategories)
 * @param {Object} props - The component properties
 * @param {CategorySearchHit[]} props.suggestions - The taxonomy suggestions to display
 * @param {function} props.onItemClick - Function called when clicking on an element
 * @param {Object} props.classes - The Material-UI style classes
 * @returns {JSX.Element|null} The taxonomy component
 */
export default function TaxonomyResults({ suggestions, onItemClick }) {
  const classes = useStyles();
  const hasSuggestions = suggestions && suggestions.length > 0;
  return (
    <div
      className={`${classes.taxonomySection} ${
        !hasSuggestions ? 'empty-suggestions' : ''
      } `}
    >
      <div className={classes.sectionHeader}>
        <h3 className={classes.sectionTitle}>
          <Layers style={{ fontSize: '20px' }} />
          Categories
          <span className={classes.sectionCount}>({suggestions.length})</span>
        </h3>
      </div>
      <div className={classes.taxonomyList}>
        <TaxonomySuggestions
          suggestions={suggestions}
          onItemClick={onItemClick}
          hasSuggestions={hasSuggestions}
          classes={classes}
        />
      </div>
    </div>
  );
}

/**
 * Component for displaying taxonomy suggestions
 * @param {Object} props - The component properties
 * @param {CategorySearchHit[]} props.suggestions - The taxonomy suggestions to display
 * @param {boolean} hasSuggestions - Indicates if there are suggestions to display
 * @param {function} props.onItemClick - Function called when clicking on an element
 * @returns {JSX.Element} The taxonomy suggestions component
 */
function TaxonomySuggestions({ suggestions, onItemClick, hasSuggestions }) {
  const classes = useStyles();
  if (!hasSuggestions) {
    return (
      <Empty Icon={LayersClear}>
        <p>No category results found.</p>
      </Empty>
    );
  }

  return (
    <>
      {suggestions.map((suggestion) => (
        <button
          key={suggestion._id}
          className={classes.taxonomyItem}
          onClick={() => onItemClick(suggestion)}
        >
          <div className={classes.taxonomyIcon}>
            {suggestion.type === 'category' ? (
              <Layers className={classes.icon} />
            ) : (
              <LayersTwoTone className={classes.icon} />
            )}
          </div>
          <div className={classes.taxonomyContent}>
            <div
              className={classes.taxonomyTitle}
              dangerouslySetInnerHTML={{ __html: suggestion._formatted.title }}
            />
            {suggestion.searchField && (
              <div
                className={classes.taxonomyHierarchy}
                dangerouslySetInnerHTML={{
                  __html: suggestion._formatted.searchField,
                }}
              />
            )}
          </div>
        </button>
      ))}
    </>
  );
}

TaxonomyResults.propTypes = {
  /** The taxonomy suggestions to display */
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['category', 'subcategory']).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      hierarchy: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  /** Function called when clicking on an element */
  onItemClick: PropTypes.func,
};

TaxonomyResults.defaultProps = {
  suggestions: [],
  onItemClick: () => {},
};
